import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  MapPin, RefreshCw, AlertTriangle, Users, Clock, Building2, Camera, Filter, Flame, ShieldAlert, Info,
} from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { hariIniISO } from "@/lib/tanggal";
import { useAuth } from "../context/AuthContext";

// Fix Leaflet default icon path issue (webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const DEFAULT_CENTER = [-2.5, 118.0]; // Indonesia center (fallback)
const REFRESH_MS = 30000;

// Custom DivIcon builder — coloured pin with initial
const employeeIcon = (color, initial) =>
  L.divIcon({
    className: "custom-emp-pin",
    html: `<div style="
      width:34px;height:34px;border-radius:50%;
      background:${color};color:white;
      display:grid;place-items:center;
      font-weight:700;font-size:13px;
      box-shadow:0 3px 8px rgba(0,0,0,.25);
      border:2px solid white;">${initial}</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    popupAnchor: [0, -18],
  });

const branchIcon = L.divIcon({
  className: "custom-branch-pin",
  html: `<div style="
    width:38px;height:38px;border-radius:8px;
    background:#0f172a;color:#facc15;
    display:grid;place-items:center;
    box-shadow:0 3px 10px rgba(0,0,0,.4);
    border:2px solid white;
    transform:rotate(0deg);">
    <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2.4'><path d='M3 21h18M5 21V7l7-4 7 4v14M9 9h1M9 13h1M9 17h1M14 9h1M14 13h1M14 17h1'/></svg>
    </div>`,
  iconSize: [38, 38],
  iconAnchor: [19, 19],
  popupAnchor: [0, -18],
});

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }) : "-");

// Auto-fit map to markers whenever they change
function AutoFit({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
  }, [points, map]);
  return null;
}

// Heatmap layer — density visualization of clock-in points
function HeatLayer({ points, enabled }) {
  const map = useMap();
  useEffect(() => {
    if (!enabled || !points || points.length === 0) return;
    // Weight each point by 1; density comes from proximity of multiple pins
    const heatPts = points.map(p => [p[0], p[1], 0.85]);
    const layer = L.heatLayer(heatPts, {
      radius: 45,
      blur: 30,
      maxZoom: 18,
      minOpacity: 0.35,
      gradient: {
        0.15: "#3b82f6", // blue (cold, isolated)
        0.35: "#22c55e", // green
        0.55: "#facc15", // yellow
        0.75: "#f97316", // orange
        1.0:  "#ef4444", // red (very crowded)
      },
    }).addTo(map);
    return () => { map.removeLayer(layer); };
  }, [points, enabled, map]);
  return null;
}

export default function LiveMap() {
  // Kunci modul HARUS sama persis dengan gerbang backend
  // (`require_role_atau_izin("live_map", "view", ...)` di routers/attendance.py),
  // supaya halaman ini tidak pernah menjanjikan peta yang ujungnya 403.
  const { can } = useAuth();
  const bolehLihat = can("live_map", "view");
  const [date, setDate] = useState(hariIniISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branchFilter, setBranchFilter] = useState("");
  const [onlySus, setOnlySus] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [heatOn, setHeatOn] = useState(false);
  // Pesan gagal disimpan, bukan cuma di-toast: toast hilang dalam beberapa detik
  // dan yang tersisa hanyalah peta kosong tanpa keterangan — pengguna menyangka
  // aplikasinya rusak, padahal servernya sudah menjelaskan sebabnya.
  const [gagal, setGagal] = useState("");

  const load = async (silent = false) => {
    if (!bolehLihat) return;
    if (!silent) setLoading(true);
    try {
      const { data: d } = await api.get(`/attendance/live-map?date_str=${date}`);
      setData(d);
      setGagal("");
    } catch (e) {
      const pesan = e?.response?.data?.detail || "Gagal muat peta absensi.";
      setGagal(pesan);
      toast.error(pesan);
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [date, bolehLihat]);
  useEffect(() => {
    if (!autoRefresh || !bolehLihat) return;
    const id = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, date, bolehLihat]);

  const pins = useMemo(() => {
    if (!data?.pins) return [];
    let list = data.pins.filter(p => p.clock_in_lat != null && p.clock_in_lng != null);
    if (branchFilter) list = list.filter(p => p.branch === branchFilter);
    if (onlySus) list = list.filter(p => p.is_suspicious);
    return list;
  }, [data, branchFilter, onlySus]);

  const branchesForMap = useMemo(() => {
    if (!data?.branches) return [];
    if (branchFilter) return data.branches.filter(b => b.branch === branchFilter);
    return data.branches;
  }, [data, branchFilter]);

  const points = useMemo(() => [
    ...pins.map(p => [p.clock_in_lat, p.clock_in_lng]),
    ...branchesForMap.map(b => [b.lat, b.lng]),
  ], [pins, branchesForMap]);

  const branchList = useMemo(() => {
    const set = new Set((data?.branches || []).map(b => b.branch));
    (data?.pins || []).forEach(p => p.branch && set.add(p.branch));
    return Array.from(set).sort();
  }, [data]);

  const susList = (data?.pins || []).filter(p => p.is_suspicious);
  const noGpsList = (data?.pins || []).filter(p => p.no_gps);

  // Semua hook di atas sudah dipanggil sebelum baris ini — jangan pindahkan
  // gerbang ini ke atas, urutan hook React tidak boleh berubah antar render.
  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-10 text-center" data-testid="livemap-tidak-punya-akses">
          <ShieldAlert className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-700 font-medium">Anda tidak punya akses ke Peta Live Absensi</p>
          <p className="text-sm text-slate-500 mt-1">Hubungi admin untuk meminta izin modul "Peta Live Absensi".</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-[1600px]">
      <header className="mb-4 flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Absensi Live</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Peta Absensi Real-time</h1>
          {/* Kalimat ini menyesuaikan diri dengan lingkup data akun (own/cabang/all —
              lihat routers/attendance.py::attendance_live_map). Menjanjikan
              "setiap karyawan" pada akun yang backend-nya hanya mengirim satu
              orang (atau satu cabang) membuat peta yang benar terlihat seperti
              peta yang rusak. */}
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">
            {data?.lingkup === "own"
              ? "Lokasi clock-in Anda hari ini vs titik kantor. Pin ⚠️ merah = di luar radius yang disetujui."
              : data?.lingkup === "cabang"
              ? "Lokasi clock-in karyawan cabang Anda hari ini vs titik kantor. Pin ⚠️ merah = di luar radius yang disetujui (perlu cek HRD)."
              : "Lokasi clock-in setiap karyawan hari ini vs titik kantor. Pin ⚠️ merah = di luar radius yang disetujui (perlu cek HRD)."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <DatePicker value={date} onChange={setDate} data-testid="livemap-date" />
          <select value={branchFilter} onChange={e => setBranchFilter(e.target.value)} data-testid="livemap-branch"
            className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
            <option value="">Semua Branch</option>
            {branchList.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <label className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-slate-200 bg-white text-xs cursor-pointer">
            <input type="checkbox" checked={onlySus} onChange={e => setOnlySus(e.target.checked)} data-testid="livemap-only-sus" />
            <AlertTriangle className="w-3.5 h-3.5 text-rose-500" /> Hanya mencurigakan
          </label>
          <label className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-slate-200 bg-white text-xs cursor-pointer">
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} data-testid="livemap-auto" />
            Auto refresh 30s
          </label>
          <button onClick={() => setHeatOn(v => !v)} data-testid="livemap-heat-toggle"
            className={`inline-flex items-center gap-1.5 h-10 px-3 rounded-lg text-xs font-medium transition-colors ${
              heatOn ? "bg-orange-500 hover:bg-orange-600 text-white shadow-sm ring-2 ring-orange-300"
                     : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}>
            <Flame className={`w-3.5 h-3.5 ${heatOn ? "text-white" : "text-orange-500"}`} />
            Heatmap {heatOn ? "ON" : "OFF"}
          </button>
          <button onClick={() => load()} data-testid="livemap-refresh"
            className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </header>

      {gagal && (
        <div className="mb-4 bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3" data-testid="livemap-gagal">
          <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-rose-900">Peta tidak bisa dimuat</p>
            <p className="text-xs text-rose-700 mt-0.5">{gagal}</p>
          </div>
        </div>
      )}

      {/* Lingkup data dijelaskan TERUS TERANG. Backend membatasi peta ini ke
          lingkup modul Absensi (lihat routers/attendance.py::attendance_live_map);
          tanpa kalimat ini, akun berlingkup "own" cuma melihat peta nyaris kosong
          dan menyimpulkan aplikasinya rusak — persis gejala "izin mati sejak
          lahir" yang sedang diperbaiki, cuma berpindah tempat. */}
      {data?.lingkup_catatan && (
        <div className="mb-4 bg-sky-50 border border-sky-200 rounded-xl p-4 flex items-start gap-3" data-testid="livemap-lingkup">
          <Info className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-sky-900">Peta ini belum menampilkan seluruh karyawan</p>
            <p className="text-xs text-sky-700 mt-0.5">{data.lingkup_catatan}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Stat icon={Users} label="Absen Hari Ini" value={data?.stats?.total ?? 0} tint="emerald" />
        <Stat icon={MapPin} label="Ber-GPS" value={data?.stats?.with_gps ?? 0} tint="sky" />
        <Stat icon={AlertTriangle} label="Di Luar Radius" value={data?.stats?.suspicious ?? 0} tint="rose" testid="stat-sus" />
        <Stat icon={Clock} label="Terlambat" value={data?.stats?.late ?? 0} tint="amber" />
        <Stat icon={Building2} label="Titik Kantor" value={data?.branches?.length ?? 0} tint="slate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Map */}
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" style={{ height: "620px" }}>
          <MapContainer center={DEFAULT_CENTER} zoom={5} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {points.length > 0 && <AutoFit points={points} />}
            <HeatLayer points={pins.map(p => [p.clock_in_lat, p.clock_in_lng])} enabled={heatOn} />

            {branchesForMap.map(b => (
              <div key={`br-${b.branch}`}>
                <Marker position={[b.lat, b.lng]} icon={branchIcon}>
                  <Popup>
                    <div className="text-xs">
                      <p className="font-bold text-slate-900 mb-0.5">🏢 {b.branch}</p>
                      <p className="text-slate-600">{b.alamat || "-"}</p>
                      <p className="text-slate-500 font-mono mt-1">{b.lat.toFixed(5)}, {b.lng.toFixed(5)}</p>
                      <p className="text-emerald-700 mt-1">Radius izin: <b>{b.radius_m}m</b></p>
                    </div>
                  </Popup>
                </Marker>
                <Circle
                  center={[b.lat, b.lng]}
                  radius={b.radius_m}
                  pathOptions={{ color: "#059669", fillColor: "#10b981", fillOpacity: 0.12, weight: 2 }}
                />
              </div>
            ))}

            {pins.map(p => {
              const color = p.is_suspicious ? "#e11d48" : ((p.late_minutes || 0) > 0 ? "#f59e0b" : "#059669");
              return (
                <Marker key={p.id} position={[p.clock_in_lat, p.clock_in_lng]}
                  icon={employeeIcon(color, (p.nama || "?")[0]?.toUpperCase() || "?")}>
                  <Popup>
                    <div className="text-xs min-w-[220px]" data-testid={`pin-${p.id}`}>
                      <p className="font-bold text-slate-900 mb-1">{p.nama}</p>
                      <p className="text-slate-600">🏢 {p.branch || "-"}</p>
                      {p.shift_name && (
                        <p className="text-slate-500 mt-0.5">
                          Shift: <b>{p.shift_name}</b> ({p.shift_jam_masuk}-{p.shift_jam_pulang})
                        </p>
                      )}
                      <div className="mt-2 border-t border-slate-200 pt-2">
                        <p className="text-emerald-700 font-medium">🕒 IN: {fmtTime(p.clock_in)}
                          {p.late_minutes > 0 && <span className="text-amber-700 ml-1">(+{p.late_minutes}m telat)</span>}
                        </p>
                        <p className={`${p.clock_out ? "text-rose-700" : "text-slate-400"} font-medium`}>
                          🕗 OUT: {fmtTime(p.clock_out)}
                        </p>
                      </div>
                      {p.distance_m != null && (
                        <div className={`mt-2 rounded p-1.5 border ${p.is_suspicious ? "bg-rose-50 border-rose-200 text-rose-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}>
                          <p className="text-[11px] font-semibold">
                            {p.is_suspicious ? "⚠️ Di luar radius" : "✓ Dalam radius"}
                          </p>
                          <p className="text-[10px]">{Math.round(p.distance_m)}m dari titik kantor</p>
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>

        {/* Right panel — legends + lists */}
        <aside className="lg:col-span-1 space-y-3">
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1.5"><Filter className="w-3.5 h-3.5" /> Legenda</p>
            <ul className="text-xs space-y-1.5">
              <li className="flex items-center gap-2"><span className="w-4 h-4 rounded-full bg-emerald-600 border-2 border-white shadow"></span> Dalam radius, tepat waktu</li>
              <li className="flex items-center gap-2"><span className="w-4 h-4 rounded-full bg-amber-500 border-2 border-white shadow"></span> Dalam radius, terlambat</li>
              <li className="flex items-center gap-2"><span className="w-4 h-4 rounded-full bg-rose-600 border-2 border-white shadow"></span> Di LUAR radius ⚠️</li>
              <li className="flex items-center gap-2"><span className="w-4 h-4 rounded bg-slate-900 border-2 border-white shadow"></span> Titik kantor + circle radius</li>
            </ul>
            {heatOn && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-orange-600 mb-1.5 inline-flex items-center gap-1"><Flame className="w-3 h-3" /> Heatmap Densitas</p>
                <div className="h-2 rounded-full" style={{ background: "linear-gradient(to right, #3b82f6 0%, #22c55e 25%, #facc15 50%, #f97316 75%, #ef4444 100%)" }}></div>
                <div className="flex justify-between text-[9px] text-slate-500 mt-1">
                  <span>Sepi</span><span>Ramai</span>
                </div>
              </div>
            )}
          </div>

          {susList.length > 0 && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-rose-800 mb-2 inline-flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Perlu Verifikasi ({susList.length})</p>
              <ul className="space-y-1 text-xs" data-testid="sus-list">
                {susList.map(p => (
                  <li key={p.id} className="border border-rose-100 bg-white rounded p-2">
                    <p className="font-semibold text-slate-900">{p.nama}</p>
                    <p className="text-slate-600">{p.branch} · {fmtTime(p.clock_in)}</p>
                    <p className="text-rose-700 font-mono text-[10px]">{Math.round(p.distance_m)}m dari kantor</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {noGpsList.length > 0 && (
            <div className="bg-slate-100 border border-slate-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-slate-800 mb-2 inline-flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" /> Tanpa GPS ({noGpsList.length})</p>
              <p className="text-[11px] text-slate-500 mb-2">Karyawan absen sebelum branch di-set geofence.</p>
              <ul className="space-y-1 text-xs" data-testid="nogps-list">
                {noGpsList.map(p => (
                  <li key={p.id} className="border border-slate-200 bg-white rounded p-2">
                    <p className="font-semibold text-slate-900">{p.nama}</p>
                    <p className="text-slate-600">{p.branch || "-"} · {fmtTime(p.clock_in)}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(data?.stats?.total ?? 0) === 0 && !loading && (
            <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
              <MapPin className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Belum ada absensi untuk tanggal ini.</p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tint = "emerald", testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 md:p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-3.5 h-3.5 text-${tint}-600`} />
        <p className="text-[10px] md:text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      </div>
      <p className={`font-display text-xl md:text-2xl font-bold text-${tint}-700`}>{value}</p>
    </div>
  );
}
